import type { FormState, UseFormRegister } from 'react-hook-form';
import styled from 'styled-components';
import type { WatermarkTemplate } from '@frameio/graphql-client/generated';
import { select } from '@frameio/i18n';
import {
  FeatureGate,
  FeatureGateConditionalRender,
  FeatureGateLabel,
} from '@frameio/plan-management/feature-gate/components';
import { useFeatureGatePlanSuggestion } from '@frameio/plan-management/feature-gate/hooks';
import { IconLockFeatureGate } from '@frameio/shared/icons';
import type { ReactAriaOrHexString } from '@frameio/vapor';
import {
  Box,
  Button2,
  IconButton,
  Segment,
  SegmentedControl,
  Select,
  Separator,
  Stack,
  Text,
  TextareaField,
  TextField,
  TextFieldLabel,
  textStyles,
  theme,
  Toggle,
  ToggleCheckbox,
  ToggleLabel,
  Tooltip,
} from '@frameio/vapor';
import {
  IconArrowLeft,
  IconArrowRight,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconLetter,
  IconPlus,
  IconTextAlignCenter,
  IconTextAlignLeft,
  IconTextAlignRight,
  IconTrash,
  IconX,
} from '@frameio/vapor-icons';
import {
  BLOCK_SIZES,
  CHECKBOX_LIST,
  MAX_BLOCKS,
  MIN_BLOCKS,
  MOTION_TYPES,
  // WATERMARK_STRING_ALLOWED_CHARS_REGEX,
  WATERMARK_STRING_CHAR_LIMIT,
  WatermarkBuilderModes,
} from '../../constants';
import type {
  MotionType,
  TextAlignment,
  WatermarkBlockData,
  WatermarkTemplateFormValues,
} from '../../types';
import { ColorSelector } from './ColorSelector';
import TEXT from './generated/TEXT/ControlsArea';
import { OpacityControl } from './OpacityControl';
import { RotationControl } from './RotationControl';

TEXT.DEFINE(
  {
    /** Label for adding a block */
    addBlock: 'Add Block',
    /** Tooltip explaining why the alignment control is disabled while a block is rotated */
    alignmentRotatedTooltip: 'Rotated text is always center-aligned.',
    /** Tooltip explaining why the alignment control is disabled while a block is scrolling */
    alignmentScrollingTooltip: 'Alignment doesn’t apply to scrolling text.',
    /** Label for color selector */
    color: 'Color',
    /** Label for identifying the current watermark block being edited */
    currentBlockLabel: (blockNumber: number) => `Block ${blockNumber}`,
    /** Label for custom text field */
    customText: 'Custom Text',
    /** Placeholder for custom text input */
    customTextPlaceholder: 'Custom text (optional)',
    /** Label for delete action */
    delete: 'Delete',
    /** Label for deleting a block */
    deleteBlock: 'Delete block',
    /** Label for display settings */
    display: 'Display',
    /** Error messages for form validation */
    errors: {
      /** Error when custom text exceeds character limit */
      customTextTooLong: `The name field cannot be more than ${WATERMARK_STRING_CHAR_LIMIT} characters`,
      /** Error when custom text contains invalid characters */
      invalidCustomText: 'Only printable unicode characters are allowed.',
      /** Error when template name is too long */
      templateNameTooLong: 'Template name is too long',
      /** Error when template name already exists */
      templateNameExists: 'Template name already exists',
      /** Error when required field is missing */
      required: (field: string) => `${field} is required`,
    },
    /** Label for template name field */
    label: 'Template name',
    /** Label for opacity control */
    opacity: 'Opacity',
    /** Placeholder for template name input */
    placeholder: 'Enter template name',
    /** Label for rotation control */
    rotation: 'Rotation',
    /** Label for size selector */
    selectSize: 'Select size',
    /** Label for session-based watermarking option */
    sessionBasedWatermarkingLabel: 'Session-based Watermark',
    /** Description of session-based watermarking */
    sessionBasedWatermarkingDescription:
      'Embed personal identifying info and custom visuals for enhanced security.',
    /** Label for size control */
    size: 'Size',
    /** Label for text alignment control */
    textAlignment: 'Alignment',
    /** Label for text scroll control */
    textScroll: 'Text Scroll',
    /** Label for text shadow control */
    textShadow: 'Text Shadow',
    /** Label for dynamic text to add to the watermark */
    watermarkDynamicText: (field: string): string =>
      select(field, {
        displayName: 'Name',
        displayDateTime: 'Date & time',
        displayEmail: 'Email',
        displayShareCreator: "Share creator's custom text",
        displayIP: 'IP Address',
        other: 'Name',
      }),
    /** Label for watermark size */
    watermarkSize: (size: string): string =>
      select(size, {
        SMALL: 'Small',
        MEDIUM: 'Medium',
        LARGE: 'Large',
        HUGE: 'Huge',
        other: 'Medium',
      }),
    /** Aria label for center text alignment */
    ariaLabelAlignCenter: 'Align center',
    /** Aria label for left text alignment */
    ariaLabelAlignLeft: 'Align left',
    /** Aria label for right text alignment */
    ariaLabelAlignRight: 'Align right',
    /** Aria label for no scroll option */
    ariaLabelScrollNone: 'None',
    /** Aria label for right-to-left scroll */
    ariaLabelScrollRightToLeft: 'Right to Left',
    /** Aria label for left-to-right scroll */
    ariaLabelScrollLeftToRight: 'Left to Right',
    /** Aria label for text shadow off */
    ariaLabelTextShadowOff: 'Disable Text Shadow',
    /** Aria label for text shadow on */
    ariaLabelTextShadowOn: 'Enable Text Shadow',
  },
  { WATERMARK_STRING_CHAR_LIMIT }
);

type DirectionEnum = 'prev' | 'next';

type ControlsAreaProps = {
  builderMode: WatermarkBuilderModes;
  blocks: WatermarkBlockData[];
  selectedBlockId: string | null;
  templateName: string;
  templateId: string;
  setSelectedBlockId: (id: string | null) => void;
  updateBlock: (block: Partial<WatermarkBlockData>) => void;
  deleteBlock: (id: string) => void;
  addBlock: () => void;
  setTemplateName: (name: string) => void;
  templates?: WatermarkTemplate[];
  formState: FormState<WatermarkTemplateFormValues>;
  register: UseFormRegister<WatermarkTemplateFormValues>;
};

const feature = 'SESSION_BASED_WATERMARKING';

export const ControlsArea = ({
  blocks,
  builderMode,
  selectedBlockId,
  setSelectedBlockId,
  updateBlock,
  deleteBlock,
  addBlock,
  formState,
  register,
  templates,
  templateId,
  setTemplateName,
}: ControlsAreaProps) => {
  const selectedBlock = blocks.find((block) => block.id === selectedBlockId);
  const { errors, touchedFields: touched } = formState;
  const isSessionBasedWatermarkEnabledInPlan =
    useFeatureGatePlanSuggestion(feature)?.includedInCurrentPlan;

  const handleCheckboxChange =
    (field: keyof WatermarkBlockData) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateBlock({ [field]: e.target.checked });
    };

  const navigateBlocks = (direction: DirectionEnum) => {
    const currentIndex = blocks.findIndex(
      (block) => block.id === selectedBlockId
    );
    let nextIndex;
    if (direction === 'prev') {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : blocks.length - 1;
    } else {
      nextIndex = currentIndex < blocks.length - 1 ? currentIndex + 1 : 0;
    }

    setSelectedBlockId(blocks[nextIndex].id);
  };

  if (!selectedBlock) return null;

  // Alignment is fixed while scrolling (left-aligned) or rotated (center-
  // aligned), so the control is disabled in both cases, each with its own
  // tooltip explaining why.
  const isScrolling = selectedBlock.motion !== MOTION_TYPES.NONE;
  const isRotated = selectedBlock.rotation !== 0;
  const isAlignmentDisabled = isScrolling || isRotated;

  const sizeOptions = Object.values(BLOCK_SIZES).map((value) => ({
    value,
    label: TEXT.watermarkSize(value),
  }));

  // Helper functions for handling opacity and rotation changes
  const handleOpacityChange = (value: number | string) => {
    let numValue = Math.round(
      typeof value === 'string' ? parseFloat(value) : value
    );
    numValue = Math.max(10, Math.min(100, numValue));
    updateBlock({ opacity: numValue });
  };

  const handleRotationChange = (value: number | string) => {
    let numValue = Math.round(
      typeof value === 'string' ? parseFloat(value) : value
    );
    numValue = Math.max(-45, Math.min(45, numValue));
    updateBlock({ rotation: numValue });
  };

  // @todo (VIEW-4385): Add validation to custom text field instead of checking only maxLength
  // const customTextValidation = register('customText', {
  //   maxLength: {
  //     value: WATERMARK_STRING_CHAR_LIMIT,
  //     message: TEXT.errors.customTextTooLong,
  //   },
  //   pattern: {
  //     value: WATERMARK_STRING_ALLOWED_CHARS_REGEX,
  //     message: TEXT.errors.invalidCustomText,
  //   },
  // });

  const templateNameValidation = register('templateName', {
    maxLength: {
      value: WATERMARK_STRING_CHAR_LIMIT,
      message: TEXT.errors.templateNameTooLong,
    },
    required: String(TEXT.errors.required('Template name')),
    validate: (value) => {
      const foundNameTemplate = templates?.find(
        (t: WatermarkTemplate) => t.name === value.replace(/\s+$/, '')
      );

      // if mode is edit, make sure the found name template is template being edited
      // otherwise show the validation error if we're in create mode.
      if (builderMode === WatermarkBuilderModes.EDIT && foundNameTemplate) {
        return foundNameTemplate?.id === templateId;
      }
      return (
        foundNameTemplate &&
        builderMode === WatermarkBuilderModes.CREATE &&
        TEXT.errors.templateNameExists
      );
    },
    onChange: (e) => setTemplateName(e.target.value.replace(/\s+$/, '')),
  });

  return (
    <ControlContainer>
      {/* Navigation and block controls */}
      <TopControlsWrapper>
        <StyledTextFieldLabel htmlFor="template-name-text-field">
          {TEXT.label}
        </StyledTextFieldLabel>
        <FullWidthTextField
          data-testid="template-name-text-field"
          error={
            touched?.templateName ? errors?.templateName?.message : undefined
          }
          id="template-name-text-field"
          placeholder={TEXT.placeholder}
          type="name"
          {...templateNameValidation}
        />
        <ControlRow>
          <NavigationControls>
            <IconButton
              icon={<IconChevronLeft />}
              label="previous"
              onClick={() => navigateBlocks('prev')}
              variant="ghost"
            />
            <Text color="tertiary" variant="body">
              {blocks.indexOf(selectedBlock) + 1}/{blocks.length}
            </Text>
            <IconButton
              icon={<IconChevronRight />}
              label="next"
              onClick={() => navigateBlocks('next')}
              variant="ghost"
            />
            <BlockInfo>
              {TEXT.currentBlockLabel(blocks.indexOf(selectedBlock) + 1)}
            </BlockInfo>
          </NavigationControls>
          <ButtonGroup>
            <Button2
              aria-label={TEXT.deleteBlock}
              data-testid="delete-block-button"
              iconBefore={<IconTrash />}
              isDisabled={blocks.length <= MIN_BLOCKS}
              onPress={() => selectedBlockId && deleteBlock(selectedBlockId)}
              size="small"
              variant="destructive"
            >
              {TEXT.delete}
            </Button2>

            <FeatureGate feature={feature} shouldShowTooltip={true}>
              <FeatureGateConditionalRender
                fallback={
                  <Button2
                    aria-label={TEXT.addBlock}
                    data-testid="add-block-button"
                    iconBefore={
                      <IconLockFeatureGate customSize={20} isActive={true} />
                    }
                    isDisabled={blocks.length >= MAX_BLOCKS}
                    size="small"
                  >
                    {TEXT.addBlock}
                  </Button2>
                }
                feature={feature}
              >
                <Button2
                  aria-label={TEXT.addBlock}
                  data-testid="add-block-button"
                  iconBefore={<IconPlus />}
                  isDisabled={blocks.length >= MAX_BLOCKS}
                  onPress={addBlock}
                  size="small"
                >
                  {TEXT.addBlock}
                </Button2>
              </FeatureGateConditionalRender>
            </FeatureGate>
          </ButtonGroup>
        </ControlRow>
      </TopControlsWrapper>
      <Stack gap={32}>
        <Separator color="primary" gap={8} />
      </Stack>

      {/* Checkbox group for session-based watermarking display options */}
      {isSessionBasedWatermarkEnabledInPlan && (
        <CheckboxGroup>
          {CHECKBOX_LIST.map(({ prop }) => (
            <CheckboxWrapper key={prop} $disabled={false}>
              <Toggle
                checked={Boolean(
                  selectedBlock[prop as keyof WatermarkBlockData]
                )}
                data-testid="display-option-checkbox"
                onChange={handleCheckboxChange(
                  prop as keyof WatermarkBlockData
                )}
              >
                <Box display="inline-flex" gap={8}>
                  <ToggleCheckbox />
                  <ToggleLabel color="secondary" variant="secondary">
                    {TEXT.watermarkDynamicText(prop)}
                  </ToggleLabel>
                </Box>
              </Toggle>
            </CheckboxWrapper>
          ))}
        </CheckboxGroup>
      )}

      {/* Custom text input */}
      <ControlGroup>
        <TextareaField
          data-testid="custom-text-textarea"
          maxLength={WATERMARK_STRING_CHAR_LIMIT}
          name="customText"
          onChange={(e) => updateBlock({ customText: e.target.value })}
          placeholder={TEXT.customTextPlaceholder}
          rowsMin={3}
          style={{
            fontSize: theme.fontSizes[70],
            color: theme.colors.text.body,
          }}
          value={selectedBlock.customText}
          // {...customTextValidation}
        />
      </ControlGroup>
      {/* Opacity and rotation controls */}
      <Box alignItems="center" display="flex" gap={12} paddingBottom={16}>
        <Box minWidth="60px">
          <Text color="tertiary" variant="body">
            {TEXT.opacity}
          </Text>
        </Box>
        <OpacityControl
          onChange={handleOpacityChange}
          value={selectedBlock.opacity}
        />
      </Box>
      <Box alignItems="center" display="flex" gap={12}>
        <Box minWidth="60px">
          <Text color="tertiary" variant="body">
            {TEXT.rotation}
          </Text>
        </Box>
        <RotationControl
          isDisabled={selectedBlock.motion !== MOTION_TYPES.NONE}
          onChange={handleRotationChange}
          value={selectedBlock.rotation}
        />
      </Box>

      {/* Size and alignment */}
      <InlineControlGroup>
        <HalfWidthControl>
          <StyledText>{TEXT.size}</StyledText>
          <Select
            content={sizeOptions}
            name="size"
            onChange={(option) => updateBlock({ size: option })}
            placeholder={TEXT.selectSize}
            value={selectedBlock.size}
            width="100%"
          >
            <FullWidthButton
              iconAfter={
                <IconChevronDown
                  color={theme.colors.icon.secondary}
                  size={16}
                />
              }
            >
              <span style={{ marginRight: 'auto' }}>
                {TEXT.watermarkSize(selectedBlock.size)}
              </span>
            </FullWidthButton>
          </Select>
        </HalfWidthControl>
        <HalfWidthControl>
          <StyledText>{TEXT.textAlignment}</StyledText>
          <Tooltip
            disabled={!isAlignmentDisabled}
            placement="top"
            title={
              isRotated
                ? TEXT.alignmentRotatedTooltip
                : TEXT.alignmentScrollingTooltip
            }
          >
            <div>
              <SegmentedControl
                accessibleLabel={TEXT.textAlignment}
                alignment="horizontal"
                isDisabled={isAlignmentDisabled}
                onChange={(nextValue) =>
                  updateBlock({ textAlignment: nextValue as TextAlignment })
                }
                size="medium"
                value={selectedBlock.textAlignment}
                variant="primary"
              >
                <Segment
                  aria-label={TEXT.ariaLabelAlignLeft}
                  icon={<IconTextAlignLeft />}
                  value="LEFT"
                />
                <Segment
                  aria-label={TEXT.ariaLabelAlignCenter}
                  icon={<IconTextAlignCenter />}
                  value="CENTER"
                />
                <Segment
                  aria-label={TEXT.ariaLabelAlignRight}
                  icon={<IconTextAlignRight />}
                  value="RIGHT"
                />
              </SegmentedControl>
            </div>
          </Tooltip>
        </HalfWidthControl>
      </InlineControlGroup>
      {/* Color and text shadow */}
      <InlineControlGroup>
        <HalfWidthControl>
          <StyledText>{TEXT.color}</StyledText>
          <ColorSelector
            onChange={(color) => updateBlock({ color: color as string })}
            value={selectedBlock.color as ReactAriaOrHexString}
          />
        </HalfWidthControl>
        <HalfWidthControl>
          <StyledText>{TEXT.textShadow}</StyledText>
          <SegmentedControl
            accessibleLabel={TEXT.textShadow}
            alignment="horizontal"
            onChange={(nextValue) =>
              updateBlock({ textShadow: nextValue === 'true' })
            }
            value={selectedBlock.textShadow ? 'true' : 'false'}
            variant="primary"
          >
            <Segment
              aria-label={TEXT.ariaLabelTextShadowOff}
              icon={<IconX />}
              value="false"
            />
            <Segment
              aria-label={TEXT.ariaLabelTextShadowOn}
              icon={<IconLetter />}
              value="true"
            />
          </SegmentedControl>
        </HalfWidthControl>
      </InlineControlGroup>
      {/* Text scroll */}
      <InlineControlGroup $removeBottomMargin={true}>
        <HalfWidthControl>
          <StyledText>{TEXT.textScroll}</StyledText>
          <SegmentedControl
            accessibleLabel={TEXT.textScroll}
            alignment="horizontal"
            onChange={(nextValue) =>
              updateBlock({ motion: nextValue as MotionType })
            }
            size="medium"
            value={selectedBlock.motion}
            variant="primary"
          >
            <Segment
              aria-label={TEXT.ariaLabelScrollNone}
              icon={<IconX />}
              value={'none'}
            />
            <Segment
              aria-label={TEXT.ariaLabelScrollRightToLeft}
              icon={<IconArrowLeft />}
              value={'right_to_left'}
            />
            <Segment
              aria-label={TEXT.ariaLabelScrollLeftToRight}
              icon={<IconArrowRight />}
              value={'left_to_right'}
            />
          </SegmentedControl>
        </HalfWidthControl>
        <HalfWidthControl />
      </InlineControlGroup>
      {/* Display dummy checkbox group if session based watermarking is not enabled */}
      {!isSessionBasedWatermarkEnabledInPlan && (
        <>
          <Stack gap={32}>
            <Separator color="primary" gap={24} />
          </Stack>
          <FeatureGate feature={feature}>
            <Box alignItems="center" display="flex" flexDirection="row">
              <Text fontWeight="medium" variant="primary">
                {TEXT.sessionBasedWatermarkingLabel}
              </Text>
              <Box paddingLeft={8}>
                <FeatureGateLabel feature={feature} />
              </Box>
            </Box>
            <Box paddingTop={8}>
              <Text color="tertiary" fontWeight="medium" variant="secondary">
                {TEXT.sessionBasedWatermarkingDescription}
              </Text>
            </Box>
            <CheckboxGroup>
              {CHECKBOX_LIST.map(({ prop }) => (
                <CheckboxWrapper key={prop} $disabled={true}>
                  <Toggle checked={false} data-testid="display-option-checkbox">
                    <Box display="inline-flex" gap={8}>
                      <ToggleCheckbox />
                      <ToggleLabel color="secondary" variant="secondary">
                        {TEXT.watermarkDynamicText(prop)}
                      </ToggleLabel>
                    </Box>
                  </Toggle>
                </CheckboxWrapper>
              ))}
            </CheckboxGroup>
          </FeatureGate>
        </>
      )}
    </ControlContainer>
  );
};

const ControlContainer = styled.div`
  border-radius: ${theme.radii[8]};
  height: 100%;
  overflow: scroll;
  padding: 24px;
  width: 100%;
`;

const CheckboxGroup = styled.div`
  display: grid;
  gap: ${theme.spaces[12]};
  grid-template-columns: 1fr 1fr;
  margin: 20px 0;
`;

const ControlGroup = styled.div`
  margin-bottom: ${theme.spaces[24]};
  margin-top: ${theme.spaces[24]};
`;

const TopControlsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spaces[8]};
  margin-bottom: ${theme.spaces[12]};
`;

const ControlRow = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spaces[8]};
`;

const NavigationControls = styled.div`
  align-items: center;
  display: flex;
`;

const BlockInfo = styled.span`
  font-size: ${theme.fontSizes[70]};
  margin: 0 10px;
`;

const InlineControlGroup = styled.div<{ $removeBottomMargin?: boolean }>`
  display: flex;
  gap: ${theme.spaces[16]};
  margin: 20px 0; /* Top and bottom margins for all list items */
  ${({ $removeBottomMargin }) => $removeBottomMargin && `margin-bottom: 0;`}
`;

const HalfWidthControl = styled.div`
  flex: 1;
`;

const FullWidthButton = styled(Button2)`
  justify-content: space-between;
  padding-left: ${theme.spaces[12]};
  padding-right: ${theme.spaces[12]};
  width: 100%;
`;

const CheckboxWrapper = styled.span<{ $disabled: boolean }>`
  max-height: 16px;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
`;

const FullWidthTextField = styled(TextField)`
  margin-bottom: ${theme.spaces[12]};
  width: 100%;
`;

const StyledTextFieldLabel = styled(TextFieldLabel)`
  ${textStyles.caption};
`;

const StyledText = styled(Text)`
  ${textStyles.caption};
  color: ${theme.colors.text.tertiary};
  display: inline-block;
  line-height: ${theme.lineHeights[100]};
  margin-bottom: ${theme.spaces[12]};
`;
